import { Reducer, useCallback, useReducer } from 'react';

import { useApolloClient } from '@apollo/client';

import { useLoyaltyRedeemPromoCodeMutation } from 'generated/graphql-gateway';
import { ILoyaltyOfferConfigsQuery, LoyaltyOfferConfigsDocument } from 'generated/sanity-graphql';
import {
  PromoCodeError,
  PromoCodeErrorReason,
} from 'pages/loyalty/loyalty-offers/loyalty-promo-code/types';
import { useSanityGqlEndpoint } from 'utils/network';

import { IncentiveSource, LoyaltyOffer } from '../types';

type State = {
  loyaltyOffer: LoyaltyOffer | null;
  error: PromoCodeError | null;
  loading: boolean;
};

enum ActionType {
  SUCCESS,
  ERROR,
  FETCHING,
  RESET,
}
type FetchingAction = { type: ActionType.FETCHING };
type ErrorAction = { type: ActionType.ERROR; payload: PromoCodeError | null };
type SuccessAction = { type: ActionType.SUCCESS; payload: LoyaltyOffer | null };
type ReducerAction = FetchingAction | ErrorAction | SuccessAction;

const initialState: State = {
  loyaltyOffer: null,
  error: null,
  loading: false,
};

const reducer: Reducer<State, ReducerAction> = (state: State, action: ReducerAction) => {
  switch (action.type) {
    case ActionType.FETCHING: {
      return { error: null, loyaltyOffer: null, loading: true };
    }
    case ActionType.SUCCESS: {
      return { error: null, loyaltyOffer: action.payload, loading: false };
    }
    case ActionType.ERROR: {
      return { error: action.payload, loyaltyOffer: null, loading: false };
    }
    default:
      return initialState;
  }
};

export const useRedeemPromoCode: () => [
  (loyaltyId: string, promoCode: string) => Promise<LoyaltyOffer | null>,
  State,
] = () => {
  const [state, dispatch] = useReducer<Reducer<State, ReducerAction>>(reducer, initialState);
  const [redeemMutation] = useLoyaltyRedeemPromoCodeMutation();

  const client = useApolloClient();
  const uri = useSanityGqlEndpoint();

  const redeemPromoCode = useCallback(
    async (loyaltyId: string, promoCode: string) => {
      dispatch({ type: ActionType.FETCHING });

      try {
        const { data } = await redeemMutation({
          variables: {
            input: {
              loyaltyId,
              code: promoCode,
              shouldRedeem: true,
            },
          },
        });

        const configId = data?.loyaltyValidatePromoCode?.configId;

        const personalizedOfferId = data?.loyaltyValidatePromoCode?.personalizedOfferId;

        if (!configId || !personalizedOfferId) {
          const reason = data?.loyaltyValidatePromoCode
            ?.reason as keyof typeof PromoCodeErrorReason;
          const error =
            (reason && PromoCodeErrorReason[reason]) || PromoCodeErrorReason.invalidCode;

          throw new PromoCodeError(error);
        }

        const { data: configOfferData } = await client.query<ILoyaltyOfferConfigsQuery>({
          query: LoyaltyOfferConfigsDocument,
          context: { uri },
          variables: {
            ids: [configId],
          },
        });

        const offerData = configOfferData?.allConfigOffer?.[0];

        if (!offerData) {
          throw new PromoCodeError(PromoCodeErrorReason.invalidCode);
        }

        const personalizedOffer = {
          ...offerData,
          loyaltyEngineId: personalizedOfferId,
          metadata: [
            { key: 'source', value: IncentiveSource.PROMOTION_CODE },
            { key: 'promoCode', value: promoCode },
          ],
        };
        dispatch({
          type: ActionType.SUCCESS,
          payload: personalizedOffer,
        });

        return personalizedOffer;
      } catch (err) {
        const error =
          err instanceof PromoCodeError
            ? err
            : new PromoCodeError(PromoCodeErrorReason.invalidCode);

        dispatch({ type: ActionType.ERROR, payload: error });

        throw error;
      }
    },
    [client, redeemMutation, uri]
  );

  return [redeemPromoCode, state];
};
